import { memo, useId, type CSSProperties } from 'react'
import { DEFAULT_MASCOT, MASCOT_COLORS, type MascotConfig } from './mascot-catalog'
import { mascotFurPalette, mascotSceneColor, mixMascotColor } from './mascot-colors'
import { MascotPattern } from './MascotDecorations'
import {
  mascotAppearance,
  type MascotExpression,
  type MascotNeeds,
} from './mascot-appearance'
import { MascotAccessory, MascotDirt, MascotToy } from './MascotDetails'
import { MascotHead, MASCOT_HEAD_PATH, JAGUAR_HEAD_PATH } from './MascotHead'
import styles from './DonorMascot.module.css'
import { FarmDrawing } from './game/FarmDrawing'
import { SheepBody, SheepHead } from './SheepMascotParts'

const PALETTES = {
  caramel: {
    light: '#f7d7a6',
    mid: '#deb17e',
    shade: '#b67d50',
    ear: '#b67d53',
    cream: '#fff1d6',
    pattern: '#956242',
  },
  cream: {
    light: '#fff5df',
    mid: '#eddabc',
    shade: '#c6a683',
    ear: '#d5b899',
    cream: '#fff9ec',
    pattern: '#b59578',
  },
  gray: {
    light: '#d8e1e5',
    mid: '#aabac5',
    shade: '#788a9c',
    ear: '#8395a8',
    cream: '#f1f2ed',
    pattern: '#5f7387',
  },
  chocolate: {
    light: '#b99178',
    mid: '#936950',
    shade: '#644737',
    ear: '#6d4e40',
    cream: '#f3dfc3',
    pattern: '#51392f',
  },
  black: {
    light: '#7b8292',
    mid: '#555c6b',
    shade: '#353a48',
    ear: '#414756',
    cream: '#dddde3',
    pattern: '#252b37',
  },
}

export const DonorMascot = memo(function DonorMascot({
  config = DEFAULT_MASCOT,
  label,
  showBackdrop = true,
  expression = 'happy',
  needs,
  motion: poseMotion,
}: {
  config?: MascotConfig
  label?: string
  showBackdrop?: boolean
  expression?: MascotExpression
  needs?: Partial<MascotNeeds>
  motion?: 'sleep-stretch' | 'wake-stretch'
}) {
  const id = `mascot-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const paint = (name: string) => `url(#${id}-${name})`
  const jaguar = config.species === 'jaguar'
  const sheep = config.species === 'sheep'
  const cat = config.species === 'cat' || jaguar
  const standing = config.pose === 'standing'
  const palette = config.furColor
    ? mascotFurPalette(config.furColor)
    : sheep && config.color === 'cream'
      ? {
          light: '#fffdf2',
          mid: '#fff5e5',
          shade: '#e3ceb2',
          ear: '#efbbc2',
          cream: '#fff9ed',
          pattern: '#dbc6a8',
        }
      : jaguar && config.color === 'honey'
        ? {
            light: '#ffe0a1',
            mid: '#dfa43e',
            shade: '#a96b29',
            ear: '#8f5b2d',
            cream: '#fff1ce',
            pattern: '#352b23',
          }
        : config.color in PALETTES
          ? PALETTES[config.color as keyof typeof PALETTES]
          : mascotFurPalette(MASCOT_COLORS[config.color])
  const eyeColor = config.eyeColor ?? (jaguar ? '#be9b39' : cat ? '#607f6c' : '#815b43')
  const sceneColor = mascotSceneColor(config)
  const a = mascotAppearance(needs, expression)
  const motion = {
    '--breath-duration': `${a.sleeping ? 4.8 : 3.2 + a.fatigue * 2}s`,
    '--tail-duration': `${a.delighted ? 0.65 : cat ? 4 + a.fatigue * 3 : 1.7 + a.fatigue * 3}s`,
    '--tail-swing': `${(a.delighted ? 13 : cat ? 6 : 11) * (1 - Math.max(a.fatigue, a.concern) * 0.8)}deg`,
    '--head-tilt': `${a.sleeping ? -9 : a.fatigue * 6 + a.concern * 2}deg`,
    '--head-drop': `${a.sleeping ? 0 : a.fatigue * 6}px`,
    '--ear-droop': `${a.fatigue * 9 + a.concern * 7}deg`,
  } as CSSProperties
  const head = sheep ? (
    <SheepHead config={config} appearance={a} paint={paint} />
  ) : (
    <MascotHead config={config} appearance={a} palette={palette} paint={paint} />
  )
  return (
    <svg
      className={styles.mascot}
      style={motion}
      viewBox="0 0 240 260"
      role="img"
      aria-label={label ?? (config.name || 'Seu mascote')}
      aria-describedby={`${id}-description`}
      xmlns="http://www.w3.org/2000/svg"
      data-pose={a.sleeping ? 'sleeping' : config.pose}
      data-species={config.species}
      data-mood={a.mood}
      data-motion={poseMotion}
      data-expression={expression}
    >
      <desc id={`${id}-description`}>{a.description}</desc>
      <defs>
        <radialGradient id={`${id}-fur`} cx="32%" cy="22%" r="85%">
          <stop stopColor={palette.light} />
          <stop offset=".58" stopColor={palette.mid} />
          <stop offset="1" stopColor={palette.shade} />
        </radialGradient>
        <linearGradient id={`${id}-ear`} x2=".8" y2="1">
          <stop stopColor={palette.mid} />
          <stop offset="1" stopColor={palette.ear} />
        </linearGradient>
        <radialGradient id={`${id}-cream`} cx="40%" cy="20%" r="90%">
          <stop stopColor="#fffaf0" />
          <stop offset="1" stopColor={palette.cream} />
        </radialGradient>
        <linearGradient id={`${id}-eye`} x2="0" y2="1">
          <stop stopColor="#35323a" />
          <stop offset=".62" stopColor={eyeColor} />
          <stop offset="1" stopColor={mixMascotColor(eyeColor, '#fff4ba', 0.42)} />
        </linearGradient>
        <radialGradient id={`${id}-blush`}>
          <stop stopColor="#f3ad9b" stopOpacity=".25" />
          <stop offset="1" stopColor="#f3ad9b" stopOpacity="0" />
        </radialGradient>
        <clipPath id={`${id}-head-clip`}>
          <path d={jaguar ? JAGUAR_HEAD_PATH : MASCOT_HEAD_PATH} />
        </clipPath>
        <clipPath id={`${id}-sleep-body-clip`}>
          <ellipse cx="143" cy="201" rx="62" ry="32" />
        </clipPath>
      </defs>
      {showBackdrop && (
        <g data-mascot-backdrop>
          <ellipse
            cx="120"
            cy="139"
            rx="108"
            ry="107"
            fill={
              config.sceneColor || config.backdrop !== 'classic'
                ? mixMascotColor(sceneColor, '#ffffff', 0.25)
                : '#f4eddf'
            }
          />
          {jaguar && config.backdrop !== 'farm' && (
            <g data-jaguar-habitat="jungle">
              <path d="M25 181q46-35 90-5t99-4q-15 69-94 73-72-1-95-64Z" fill="#3e6950" />
              <g fill="#345c43" stroke="#254c36" strokeWidth="1">
                <path d="M33 190Q2 110 39 54q32 17 10 54-18 28-16 82Z" />
                <path d="M203 190q41-63 5-123-37 15-15 51 17 30 10 72Z" />
                <path d="M31 128Q9 73 35 41q35 16 13 50Z" fill="#5d8952" />
                <path d="M207 130q21-58-5-83-36 18-12 48Z" fill="#5d8952" />
              </g>
              <path
                d="M30 191q-8-52 10-92m162 91q12-36-3-77"
                stroke="#a1b66e"
                strokeWidth="2"
                fill="none"
              />
              <g fill="#87a769">
                <ellipse cx="48" cy="224" rx="29" ry="8" transform="rotate(22 48 224)" />
                <ellipse
                  cx="195"
                  cy="224"
                  rx="26"
                  ry="7"
                  transform="rotate(-20 195 224)"
                />
              </g>
            </g>
          )}
          {config.backdrop === 'farm' && (
            <g data-mascot-farm-backdrop>
              <defs>
                <clipPath id={`${id}-farm-preview`}>
                  <ellipse cx="120" cy="139" rx="108" ry="107" />
                </clipPath>
              </defs>
              <g clipPath={`url(#${id}-farm-preview)`}>
                <svg
                  x="12"
                  y="32"
                  width="216"
                  height="214"
                  viewBox="0 0 960 600"
                  preserveAspectRatio="xMidYMid slice"
                >
                  <FarmDrawing
                    room="home"
                    sleeping={a.sleeping}
                    growing={false}
                    decor="meadow"
                    tint={config.sceneColor}
                    preview
                  />
                </svg>
              </g>
            </g>
          )}
          {config.backdrop === 'meadow' && (
            <path
              d="M27 181q46-35 90-5t94 3q-17 67-91 67-68 0-93-65Z"
              fill={mixMascotColor(sceneColor, '#628559', 0.25)}
            />
          )}
          {config.backdrop === 'sunset' && (
            <circle cx="173" cy="77" r="25" fill="#ffe5a1" />
          )}
          {config.backdrop === 'clouds' && (
            <g fill="#fff" opacity=".8">
              <path d="M28 86q-9-16 6-19 2-22 22-13 15-7 19 9 18 2 10 23Z" />
              <path d="M158 104q-9-15 6-19 2-21 20-12 15-6 18 9 16 2 10 22Z" />
            </g>
          )}
          {config.backdrop === 'stars' && (
            <g fill="#fff3bd">
              <path d="m42 70 3 8 9 2-8 5 1 9-7-6-8 4 3-9-6-6 9 0Z" />
              <path d="m195 106 3 8 9 2-8 5 1 9-7-6-8 4 3-9-6-6 9 0Z" />
            </g>
          )}
          <circle cx="29" cy="82" r="4" fill="#e8d6b7" />
          <circle cx="212" cy="175" r="3" fill="#e8d6b7" />
          <path d="m202 65 2 6 6 2-6 2-2 6-2-6-6-2 6-2Z" fill="#d8c398" />
        </g>
      )}
      <ellipse
        cx="121"
        cy="239"
        rx={a.sleeping ? 87 : 67}
        ry="8"
        fill="#594331"
        opacity=".1"
      />
      {a.sleeping && (
        <g>
          <ellipse
            cx="121"
            cy="233"
            rx="98"
            ry="16"
            fill={jaguar ? '#38593a' : '#b4acbc'}
          />
          <ellipse
            cx="121"
            cy="228"
            rx="98"
            ry="16"
            fill={jaguar ? '#7b9352' : '#e6dce9'}
            stroke={jaguar ? '#58733c' : '#c8b9cf'}
            strokeWidth="1.5"
          />
          <path
            d="M33 231q86 24 177 0"
            fill="none"
            stroke="#f9eff8"
            strokeWidth="1.5"
            strokeDasharray="3 4"
          />
        </g>
      )}
      <g className={styles.breathe}>
        {sheep ? (
          <SheepBody config={config} appearance={a} paint={paint} head={head} />
        ) : a.sleeping ? (
          <g>
            <ellipse
              cx="143"
              cy="201"
              rx="62"
              ry="32"
              fill={paint('fur')}
              stroke={palette.shade}
              strokeWidth="1"
            />
            {config.pattern === 'patches' && (
              <ellipse
                cx="163"
                cy="191"
                rx="21"
                ry="17"
                fill={palette.pattern}
                opacity=".55"
              />
            )}
            {config.pattern === 'stripes' && (
              <path
                d="m143 172-3 14m18-10-4 13m18-7-7 12"
                fill="none"
                stroke={palette.pattern}
                strokeWidth="4"
                strokeLinecap="round"
                opacity=".5"
              />
            )}
            <path
              d="M167 213q52 3 36-36-8-16-17-5"
              fill="none"
              stroke={palette.shade}
              strokeWidth="17"
              strokeLinecap="round"
            />
            <path
              d="M167 210q50 4 34-34"
              fill="none"
              stroke={palette.mid}
              strokeWidth="13"
              strokeLinecap="round"
            />
            {jaguar && (
              <path
                d="m177 211 2 7m12-11 5 6m7-17 7 2m-10-20 6-3"
                stroke="#352b23"
                strokeWidth="5"
                strokeLinecap="round"
                fill="none"
              />
            )}
            <g clipPath={paint('sleep-body-clip')}>
              <g transform="translate(34 42) scale(.8)">
                <MascotPattern
                  pattern={jaguar ? 'rosettes' : config.pattern}
                  color={jaguar ? '#352b23' : palette.pattern}
                  cream={palette.cream}
                />
              </g>
            </g>
            <ellipse cx="122" cy="225" rx="25" ry="9" fill={paint('cream')} />
            <ellipse cx="68" cy="225" rx="26" ry="9" fill={paint('fur')} />
            <path
              d="M57 224v4m8-4v5m46-5v4m8-4v5"
              stroke={palette.shade}
              strokeWidth="1.2"
              strokeLinecap="round"
              opacity=".6"
            />
            <g transform="translate(14 110) scale(.69)">
              <g transform="rotate(-65 120 158)">
                <MascotAccessory
                  accessory={config.accessory}
                  accessoryColor={config.accessoryColor}
                />
              </g>
              {head}
            </g>
            <g transform="translate(34 42) scale(.8)">
              <MascotDirt amount={a.dirt} />
            </g>
          </g>
        ) : (
          <>
            <g className={styles.tail}>
              <path
                d={
                  cat
                    ? 'M155 207C218 217 218 165 201 157q-14-6-15 8'
                    : 'M155 209q44-7 40-44 0-9-8-8-10 3-8 16'
                }
                fill="none"
                stroke={palette.shade}
                strokeWidth={jaguar ? 20 : cat ? 17 : 23}
                strokeLinecap="round"
              />
              <path
                d={cat ? 'M156 204C213 213 213 169 199 163' : 'M157 204q37-7 32-39'}
                fill="none"
                stroke={palette.mid}
                strokeWidth={cat ? 13 : 17}
                strokeLinecap="round"
              />
              {jaguar && (
                <path
                  d="M171 207l1 9m14-12 4 8m10-18 7 5m-3-18 8 1"
                  fill="none"
                  stroke="#352b23"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              )}
              {cat && (
                <path
                  d="M198 158q-10-2-12 7"
                  fill="none"
                  stroke={jaguar ? '#352b23' : palette.cream}
                  strokeWidth="12"
                  strokeLinecap="round"
                />
              )}
            </g>
            {!standing && (
              <g fill={paint('fur')} stroke={palette.shade} strokeWidth="1">
                <ellipse cx="78" cy="216" rx="24" ry="20" />
                <ellipse cx="162" cy="216" rx="24" ry="20" />
              </g>
            )}
            <path
              d={
                jaguar
                  ? standing
                    ? 'M84 144q36-17 72 0c18 19 21 51 12 70H72c-9-19-6-51 12-70Z'
                    : 'M85 145q35-18 70 0c22 17 38 58 20 77-20 18-90 18-110 0-18-19-2-60 20-77Z'
                  : standing
                    ? 'M91 144q29-14 58 0c13 16 18 49 9 66H82c-9-17-4-50 9-66Z'
                    : 'M91 146q29-14 58 0c17 18 30 56 16 73-16 16-74 16-90 0-14-17-1-55 16-73Z'
              }
              fill={paint('fur')}
              stroke={palette.shade}
              strokeWidth="1"
            />
            <path
              d="M105 161q15 7 30 0c13 12 17 35 9 47-8 15-40 15-48 0-8-12-4-35 9-47Z"
              fill={paint('cream')}
            />
            {config.pattern === 'patches' && (
              <path
                d="M149 177q19 8 13 29-11 14-20-5-6-16 7-24"
                fill={palette.pattern}
                opacity=".55"
              />
            )}
            {config.pattern === 'stripes' && (
              <g
                fill="none"
                stroke={palette.pattern}
                strokeWidth="4"
                strokeLinecap="round"
                opacity=".5"
              >
                <path d="m80 182 13 5m-15 6 12 5m70-16-13 5m15 6-12 5" />
              </g>
            )}
            <g
              className={styles.frontLegs}
              fill={paint('fur')}
              stroke={palette.shade}
              strokeWidth="1.1"
            >
              <path
                d={
                  standing
                    ? 'M99 185c-10 0-14 12-14 27v14c-4 13 25 14 27 3v-28q0-16-13-16Z'
                    : 'M99 186c-11 0-14 11-13 24l1 14c-5 14 24 17 25 3v-25q0-16-13-16Z'
                }
                stroke="none"
              />
              <path
                d={
                  standing
                    ? 'M141 185c10 0 14 12 14 27v14c4 13-25 14-27 3v-28q0-16 13-16Z'
                    : 'M141 186c11 0 14 11 13 24l-1 14c5 14-24 17-25 3v-25q0-16 13-16Z'
                }
                stroke="none"
              />
            </g>
            <g fill={paint('cream')}>
              <ellipse cx="99" cy="229" rx={jaguar ? 18 : 15} ry="8" />
              <ellipse cx="141" cy="229" rx={jaguar ? 18 : 15} ry="8" />
            </g>
            <g
              stroke={palette.shade}
              strokeWidth="1.15"
              strokeLinecap="round"
              opacity=".55"
            >
              <path d="M93 228v5m7-5v6m34-6v6m7-6v5" />
            </g>
            <path
              d="m113 187 3 4 4-3 4 3 3-4"
              fill="none"
              stroke={palette.shade}
              strokeWidth="1.3"
              strokeLinecap="round"
              opacity=".25"
            />
            <MascotPattern
              pattern={jaguar ? 'rosettes' : config.pattern}
              color={jaguar ? '#352b23' : palette.pattern}
              cream={palette.cream}
            />
            <MascotDirt amount={a.dirt} />
            <MascotAccessory
              accessory={config.accessory}
              accessoryColor={config.accessoryColor}
            />
            <g transform={standing ? 'translate(0 -5)' : undefined}>{head}</g>
          </>
        )}
      </g>
      <MascotToy toy={config.toy} toyColor={config.toyColor} />
    </svg>
  )
})
